/* eslint-disable no-console */
import DefaultAxios from 'src/services/axios';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import ServiceContainer from 'src/ServiceContainer';
import { API_BASE_URL } from 'src/state/ViewConfig/ViewConfig.types';
import z from 'zod';

enum LoggingSeverity {
  SEVERITY_TRACE = 'TRACE',
  SEVERITY_INFO = 'INFO',
  SEVERITY_DEBUG = 'DEBUG',
  SEVERITY_WARN = 'WARN',
  SEVERITY_ERROR = 'ERROR',
}

interface LoggingPayload {
  severity: LoggingSeverity;
  message: string;
  payload: {
    stack: string | undefined;
    /** The navigation path that the error took place, `window.location.hash`  */
    location: string;
  };
  userId?: string;
  sessionId?: string;
}

interface LegacyLoggingPayload {
  severity: LoggingSeverity;
  message: string;
  payload?: string;
  userId?: string;
  sessionId?: string;
}

const isAxiosError = (error: object): error is AxiosError => {
  return 'isAxiosError' in error;
};

export const errorToLoggingPayload = (error?: unknown): string => {
  const noErrorInformationMessage = 'No additional error information';
  const unknownErrorInformationMessage = 'Unknown Error Occured';

  if (error === undefined || error === null) {
    return noErrorInformationMessage;
  }
  // "string" | "number" | "bigint" | "boolean" | "symbol" | "undefined" | "object" | "function"
  switch (typeof error) {
    case 'function':
    case 'undefined':
      return noErrorInformationMessage;
    case 'bigint':
    case 'number':
    case 'boolean':
    case 'symbol':
      return error.toString();
    case 'string':
      return error;
    case 'object':
      const errorObj = error;
      if (errorObj instanceof Error) {
        return errorObj.stack ?? errorObj.message;
      }
      if (isAxiosError(errorObj)) {
        const serverResponse = errorObj.response ? errorToLoggingPayload(errorObj.response) : null;
        const serverCode = errorObj.code ? errorObj.code : 'No Code Returned';
        return serverResponse ? `HTTP Error: ${serverCode} Response: ${serverResponse}` : `HTTP Error: ${serverCode}`;
      }
      if (error instanceof z.ZodError) {
        return z.treeifyError(error).toString();
      }
      return unknownErrorInformationMessage;
    default:
      return unknownErrorInformationMessage;
  }
};

export function logError(message: string, error?: unknown) {
  ServiceContainer.loggingService.error(message, errorToLoggingPayload(error));
}
export function logWarn(message: string, error?: unknown) {
  ServiceContainer.loggingService.warn(message, errorToLoggingPayload(error));
}
export function logInfo(message: string, error?: unknown) {
  ServiceContainer.loggingService.info(message, errorToLoggingPayload(error));
}

export function toastAndLog(message: string, error?: unknown) {
  if (error) {
    toast.error(message);
    logError(message, error);
  } else {
    toast.info(message);
    ServiceContainer.loggingService.info(message);
  }
}

/**
 * Truncate stack message to prevent loss of log information
 * due to sending message body that exceeds the backend's max size.
 */
function formatStack(rawStack: string | undefined): string | undefined {
  const stackLength = rawStack?.length ?? 0;
  return stackLength > 10_000 ? rawStack?.slice(0, 10_000) : rawStack;
}

class LoggingService {
  // TODO: pull the userid from the request and pass it in as a constructor param
  // to be send to the server
  private readonly client: AxiosInstance;
  private readonly useLegacyTransport: boolean;

  constructor(client?: AxiosInstance) {
    this.client = client ?? DefaultAxios;
    this.useLegacyTransport = client !== undefined;
  }

  public static errorToLoggingPayload(error?: unknown): string {
    return errorToLoggingPayload(error);
  }

  private getLogOutput(message: string, stack?: string) {
    return stack ? `${message}\n${stack}` : `${message}`;
  }

  private normalizeError(error?: unknown): string | undefined {
    if (error === undefined) {
      return undefined;
    }
    if (typeof error === 'string') {
      return formatStack(error);
    }
    return formatStack(errorToLoggingPayload(error));
  }

  public trace(message: string, error?: unknown, skipLocalLog = false) {
    const rawStack = this.normalizeError(error);
    if (!skipLocalLog && !this.useLegacyTransport) {
      const output = this.getLogOutput(message, rawStack);
      console.trace(output);
    }

    return this.postLogs(LoggingSeverity.SEVERITY_TRACE, message, rawStack);
  }
  public info(message: string, error?: unknown, skipLocalLog = false) {
    const rawStack = this.normalizeError(error);
    if (!skipLocalLog && !this.useLegacyTransport) {
      const output = this.getLogOutput(message, rawStack);
      console.info(output);
    }

    return this.postLogs(LoggingSeverity.SEVERITY_INFO, message, rawStack);
  }
  public debug(message: string, error?: unknown, skipLocalLog = false) {
    const rawStack = this.normalizeError(error);
    if (!skipLocalLog && !this.useLegacyTransport) {
      const output = this.getLogOutput(message, rawStack);
      console.debug(output);
    }

    return this.postLogs(LoggingSeverity.SEVERITY_DEBUG, message, rawStack);
  }
  public warn(message: string, error?: unknown, skipLocalLog = false) {
    const rawStack = this.normalizeError(error);
    if (!skipLocalLog && !this.useLegacyTransport) {
      const output = this.getLogOutput(message, rawStack);
      console.warn(output);
    }

    return this.postLogs(LoggingSeverity.SEVERITY_WARN, message, rawStack);
  }
  public error(message: string, error?: unknown, skipLocalLog = false) {
    const rawStack = this.normalizeError(error);
    if (!skipLocalLog && !this.useLegacyTransport) {
      const output = this.getLogOutput(message, rawStack);
      console.error(output);
    }

    return this.postLogs(LoggingSeverity.SEVERITY_ERROR, message, rawStack);
  }
  private getPayload(rawStack?: string) {
    // omit the actual hash character `#` from logging
    const location = window.location.hash.slice(1);

    return {
      stack: formatStack(rawStack),
      location,
    };
  }
  private postLogs(severity: LoggingSeverity, message: string, stack?: string) {
    // remote server takes a list of errors
    const payload = this.getPayload(stack);
    // TODO: batch errors and send them in one request
    const log: LoggingPayload | LegacyLoggingPayload = this.useLegacyTransport
      ? {
          severity,
          message,
          payload: payload ?? errorToLoggingPayload(),
        }
      : {
          severity,
          message,
          payload,
        };
    const endpoint = this.useLegacyTransport ? `${API_BASE_URL}/logs` : '/api/log';

    return this.client.post<AxiosResponse>(endpoint, [log]).then((response) => response.data);
  }
}

export default LoggingService;
